import { useCallback, useEffect, useState } from 'react'
import { Gift, Loader2, X } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { fetchCustomerLoyalty } from '@/lib/api/customerLoyalty'
import { cn } from '@/lib/utils'

/**
 * @param {object} props
 * @param {number | null} props.customerId
 * @param {import('@/types/api').Sale | null} props.sale
 * @param {boolean} props.disabled
 * @param {(points: number) => Promise<import('@/types/api').Sale | null>} props.onRedeem
 * @param {() => Promise<import('@/types/api').Sale | null>} props.onRemoveRedeem
 * @param {string} [props.error]
 */
export function PosLoyaltyPanel({
  customerId,
  sale,
  disabled,
  onRedeem,
  onRemoveRedeem,
  error = '',
}) {
  const [loyalty, setLoyalty] = useState(null)
  const [loading, setLoading] = useState(false)
  const [points, setPoints] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [localError, setLocalError] = useState('')

  const loadLoyalty = useCallback(async () => {
    if (!customerId) {
      setLoyalty(null)
      return
    }

    setLoading(true)
    try {
      const summary = await fetchCustomerLoyalty(customerId)
      setLoyalty(summary)
    } catch {
      setLoyalty(null)
    } finally {
      setLoading(false)
    }
  }, [customerId])

  useEffect(() => {
    loadLoyalty()
  }, [loadLoyalty])

  useEffect(() => {
    if ((sale?.loyalty_points_redeemed ?? 0) === 0) {
      setPoints('')
    }
  }, [sale?.loyalty_points_redeemed, sale?.id])

  if (!customerId || loading) {
    return loading ? (
      <div className="flex items-center gap-2 py-1 text-[11px] text-muted-foreground">
        <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
        Loading loyalty…
      </div>
    ) : null
  }

  if (!loyalty?.enabled) {
    return null
  }

  const redeemed = Number(sale?.loyalty_points_redeemed ?? 0)
  const discount = Number(sale?.discount_amount ?? 0)
  const hasRedemption = redeemed > 0

  async function handleRedeem(event) {
    event.preventDefault()
    const value = Number(points)

    if (!Number.isFinite(value) || value < 1) {
      setLocalError('Enter points to redeem.')
      return
    }

    setSubmitting(true)
    setLocalError('')
    try {
      const updated = await onRedeem(value)
      if (updated) {
        await loadLoyalty()
      }
    } finally {
      setSubmitting(false)
    }
  }

  async function handleRedeemMax() {
    if (!loyalty?.can_redeem) {
      return
    }

    setSubmitting(true)
    setLocalError('')
    try {
      const updated = await onRedeem(loyalty.points_balance)
      if (updated) {
        await loadLoyalty()
      }
    } finally {
      setSubmitting(false)
    }
  }

  async function handleRemove() {
    setSubmitting(true)
    setLocalError('')
    try {
      const updated = await onRemoveRedeem()
      if (updated) {
        setPoints('')
        await loadLoyalty()
      }
    } finally {
      setSubmitting(false)
    }
  }

  const displayError = error || localError

  return (
    <div className="mt-1.5 rounded-lg border border-[#E5E7EB] bg-white p-2">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 text-[11px] font-semibold text-foreground">
          <Gift className="h-3.5 w-3.5 text-primary" aria-hidden />
          <span>{loyalty.points_balance} pts</span>
        </div>
        {hasRedemption ? (
          <button
            type="button"
            disabled={disabled || submitting}
            onClick={handleRemove}
            className="flex items-center gap-1 text-[10px] font-medium text-muted-foreground hover:text-foreground disabled:opacity-50"
          >
            <X className="h-3 w-3" aria-hidden />
            Remove
          </button>
        ) : null}
      </div>

      {hasRedemption ? (
        <p className="mt-1 text-[10px] text-emerald-700">
          Redeemed {redeemed} pts (−AED {discount.toFixed(2)})
        </p>
      ) : loyalty.can_redeem ? (
        <form className="mt-1.5 flex gap-1" onSubmit={handleRedeem}>
          <input
            type="number"
            min="1"
            step="1"
            value={points}
            onChange={(event) => {
              setPoints(event.target.value)
              setLocalError('')
            }}
            placeholder="Points"
            disabled={disabled || submitting}
            className={cn(
              'h-8 min-w-0 flex-1 rounded-md border border-[#E5E7EB] px-2 text-[11px]',
              'focus:outline-none focus:ring-1 focus:ring-ring',
            )}
          />
          <Button
            type="submit"
            size="sm"
            variant="outline"
            className="h-8 shrink-0 px-2 text-[10px]"
            disabled={disabled || submitting}
          >
            Redeem
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="h-8 shrink-0 px-2 text-[10px]"
            disabled={disabled || submitting}
            onClick={handleRedeemMax}
          >
            Max
          </Button>
        </form>
      ) : (
        <p className="mt-1 text-[10px] text-muted-foreground">
          Need {loyalty.min_redeem_points} pts to redeem (worth up to AED {loyalty.max_redeem_discount}).
        </p>
      )}

      {displayError ? (
        <p className="mt-1 text-[10px] text-destructive" role="alert">
          {displayError}
        </p>
      ) : null}
    </div>
  )
}
